import { SUCC_COLOR } from '../display';
import { cardStatus } from '../domain/agentStatus';
import { heatCellClass } from '../domain/heatmap';
import type { AgentHeat } from '../domain/heatmapData';
import type { AgentGroup, HeatValue } from '../types';

interface HeatmapPanelProps {
  groups: AgentGroup[];
  /** Hour columns from the API's reporting timezone, oldest first. */
  hours: number[];
  /** Measured cells per agent; an agent the heatmap did not report simply has an empty row. */
  heat: Record<string, AgentHeat>;
  timezone: string;
  isError: boolean;
  titleOf: (slug: string) => string;
  codeOf: (id: string) => string | null;
  onCopyCode: (code: string) => void;
  onOpen: (id: string) => void;
}

/** 24h activity grid; mirrors tab and filter state but never section folding. */
const HeatmapPanel = ({
  groups,
  hours,
  heat,
  timezone,
  isError,
  titleOf,
  codeOf,
  onCopyCode,
  onOpen,
}: HeatmapPanelProps) => (
  <div className="heat-panel">
    <div className="panel-h">
      <span className="t">Activity · last {hours.length}h</span>
      <span className="sub">{isError ? 'activity data unavailable' : 'where the gaps are'}</span>
    </div>
    <div className="heat" id="heat">
      {groups
        .filter((group) => group.cards.length > 0)
        .map((group) => (
          <div key={group.slug}>
            <div className="heat-sec">{titleOf(group.slug)}</div>
            {group.cards.map((card) => {
              const code = codeOf(card.id);
              const succ = card.succText.replace(/success/i, '').trim() || '—';
              return (
                <div className="heat-row" key={card.id} data-open={card.id}>
                  <span className="hl">
                    {code ? (
                      <span
                        className="hl-code"
                        role="button"
                        tabIndex={-1}
                        title="Click to copy"
                        onClick={() => onCopyCode(code)}
                      >
                        {code}
                      </span>
                    ) : null}{' '}
                    <span className="hl-name" role="button" tabIndex={-1} onClick={() => onOpen(card.id)}>
                      {card.name}
                    </span>
                  </span>
                  {(heat[card.id]?.cells ?? hours.map(() => 0 as HeatValue)).map((cell, i) => (
                    <span key={i} className={`hc ${heatCellClass(cell)}`} />
                  ))}
                  <span className="hstat">
                    <span className="hr">{card.runsText}</span> ·{' '}
                    <span className="hs" style={{ color: SUCC_COLOR[cardStatus(card)] }}>
                      {succ}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      <div className="heat-hours">
        <span className="hl">UTC{timezone}</span>
        {hours.map((h) => (
          <span key={h}>{String(h).padStart(2, '0')}</span>
        ))}
        <span />
      </div>
    </div>
  </div>
);

export default HeatmapPanel;
